import { createSelector } from "reselect";
import { ElementType, IElementDictionary } from "processEditor/model/IProcessData";
import { VariableSchemasState } from "processEditor/reducers/VariableSchemasReducer";
import { detectStaleMappings, resolveVdmSourceSchema, type VdmMappingEntry } from "./VdmMapperData";
import { parseJsonSchemaToProperties } from "./components/jsonSchemaToProperties";

export const VDM_MAPPINGS_KEY = "vdmMappings";
export const VDM_SOURCE_KEY = "sourceNodeId";
export const VDM_MAPPINGS_HISTORY_KEY = "vdmMappingsHistory";
export const VDM_VALIDATE_RESULT_SCHEMA_KEY = "validateResultSchema";
export const VDM_SCHEMA_KEY = "schema";
/** Target schema as resolved by the panel's parser - see vdmResolvedSchema.ts. Derived from VDM_SCHEMA_KEY, never edited directly. */
export const VDM_SCHEMA_TREE_KEY = "schemaTree";
// Keys whose update does not need to trigger process validation on its own. VDM_SCHEMA_TREE_KEY is
// derived from VDM_SCHEMA_KEY and always committed together with it, and that one does validate.
export const VDM_KEYS: string[] = [VDM_MAPPINGS_KEY, VDM_SOURCE_KEY, VDM_MAPPINGS_HISTORY_KEY, VDM_VALIDATE_RESULT_SCHEMA_KEY, VDM_SCHEMA_TREE_KEY];

export const VDM_SCHEMA_PATH = `parameters.${VDM_SCHEMA_KEY}`;
export const VDM_SOURCE_PATH = `parameters.${VDM_SOURCE_KEY}`;
export const VDM_MAPPINGS_PATH = `parameters.${VDM_MAPPINGS_KEY}`;
export const VDM_MAPPINGS_HISTORY_PATH = `parameters.${VDM_MAPPINGS_HISTORY_KEY}`;
export const VDM_VALIDATE_RESULT_SCHEMA_PATH = `parameters.${VDM_VALIDATE_RESULT_SCHEMA_KEY}`;

/**
 * Memoized selector that computes the set of VDM element IDs with broken (stale) mappings.
 * Only recomputes when `state.elements` or `state.variableSchemas` change.
 */
export const selectBrokenVdmElementIds = createSelector(
    [(state: { elements: IElementDictionary; variableSchemas: VariableSchemasState }) => state.elements,
     (state: { elements: IElementDictionary; variableSchemas: VariableSchemasState }) => state.variableSchemas],
    (elements, variableSchemas): ReadonlySet<string> => {
        const broken = new Set<string>();

        for (const [key, element] of Object.entries(elements)) {
            if (element.type !== ElementType.Vdm) {
                continue;
            }

            const mappingsRaw = element.parameters?.vdmMappings as string | undefined;
            if (!mappingsRaw || mappingsRaw === "{}") {
                continue;
            }

            let mappingExpressions: Record<string, VdmMappingEntry>;
            try {
                const parsed = JSON.parse(mappingsRaw);
                if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
                    continue;
                }
                mappingExpressions = parsed as Record<string, VdmMappingEntry>;
            } catch {
                continue;
            }

            if (Object.keys(mappingExpressions).length === 0) {
                continue;
            }

            const sourceSchema = resolveVdmSourceSchema(elements, variableSchemas, element);
            const targetSchemaContent = element.parameters?.schema as string | undefined;

            if (!sourceSchema || !targetSchemaContent) {
                continue;
            }

            const parsedSource = parseJsonSchemaToProperties(sourceSchema);
            const parsedTarget = parseJsonSchemaToProperties(targetSchemaContent);

            if (parsedSource.length === 0 || parsedTarget.length === 0) {
                continue;
            }

            try {
                const staleInfo = detectStaleMappings(mappingExpressions, parsedSource, parsedTarget);
                if (staleInfo.hasStale) {
                    broken.add(key);
                }
            } catch {
                continue;
            }
        }

        return broken;
    },
);
